/**
 * BexioLayout — the shared shell every Bexio page renders inside.
 *
 * Same shape as the other route layouts in this app (`AdminLayout`,
 * `PlatformLayout`, `TenantLayout`, `LandlordLayout`): a `<Route element>` that
 * draws the chrome and renders its children through `<Outlet />`. That is what
 * lets it wrap `BexioDraftsPage` without that page knowing it exists.
 *
 * It owns CHROME ONLY — title, workspace navigation, spacing. No Bexio data, no
 * sync state, no preflight, no ledger. The pages keep their own fetching and
 * their own actions; nothing here reaches across the `Outlet` boundary, so no
 * page's behaviour depends on this file.
 *
 * Notably absent, because the data behind them does not exist today: the global
 * search field, the notification badge and the API/tenant/environment status
 * bar from the design references. They are left out rather than mocked.
 */
import React from "react";
import { NavLink, Outlet } from "react-router-dom";

import { BEXIO_NAV_ITEMS } from "./bexioNav";

/**
 * One workspace tab.
 *
 * A real `NavLink`, not a `role="tab"` button: these change the URL, so they
 * have to be links — keyboard reachable, middle-clickable, and announced as the
 * current page by `NavLink`'s own `aria-current="page"`.
 *
 * The active state is carried by THREE signals, not just colour: the underline,
 * the heavier weight, and `aria-current`. Colour alone would leave the active
 * tab unidentifiable to anyone who cannot separate the two hues.
 */
function tabClass({ isActive }) {
  const base =
    "-mb-px whitespace-nowrap border-b-2 px-1 py-3 text-sm no-underline transition-colors " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 " +
    "focus-visible:ring-offset-background";
  return isActive
    ? `${base} border-primary font-semibold text-primary`
    : `${base} border-transparent font-medium text-muted-foreground hover:border-border hover:text-foreground`;
}

export default function BexioLayout() {
  return (
    <div className="min-h-full bg-background">
      {/* max-w matches what AdminBexioPage already constrains itself to, so the
          header, the tabs and the page content share one left edge and one
          right edge instead of three. */}
      <div className="mx-auto max-w-[1400px]">
        <header className="px-4 pt-4 sm:px-6 sm:pt-6">
          <h1 className="text-xl font-semibold text-foreground">Bexio</h1>
          <p className="mt-1 max-w-[720px] text-sm text-muted-foreground">
            Verwalten, synchronisieren und überwachen Sie Ihre Bexio-Daten in
            Vantio.
          </p>
        </header>

        {/* `overflow-x-auto` + `whitespace-nowrap` on the tabs: on a narrow
            screen the bar scrolls sideways as one row. Letting it wrap would
            stack half-rows of tabs above the content and push the page down. */}
        <nav
          aria-label="Bexio-Navigation"
          className="mt-4 overflow-x-auto border-b border-border px-4 sm:px-6"
        >
          <ul className="flex list-none items-center gap-6 p-0">
            {BEXIO_NAV_ITEMS.map((item) => (
              <li key={item.key}>
                <NavLink to={item.to} end={item.end} className={tabClass}>
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <Outlet />
      </div>
    </div>
  );
}
